import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FOUNDATION_ROUTES, ProviderAccreditationConfigurationService, shouldRenderMobile } from '@philips-emr/hpms-provider-accreditation-commons';
import { Link, SocialNetworksAPI, TextAPI } from '@philips-emr/tws-components';
import { DismissStyle, ThemeTonalRange, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnCatch, OnClick, OnMount } from '@philips-emr/tws-core/lib/client';
import { PromiseEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, ModalService, msg, ThemeService, ToastService } from '@philips-emr/tws-core/lib/services';
import { ModalProps } from '@philips-emr/tws-core/lib/services/modal-service';
import { LoginAPI } from '@philips-emr/tws-foundation';
import { getBreakpoint } from '@philips-emr/tws-utils';

import { LoginAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import './login-controller.css';

interface User {
  nrSequence: number,
}

@Controller({ fragment: FragmentNames.LOGIN })
export default class LoginController extends BaseController{

  @Inject(Aliases.LOGIN_COMPONENT)
  private loginAPI: LoginAPI;

  @Inject(Aliases.TEXT_INFO)
  private textInfoAPI: TextAPI;

  @Inject(Aliases.SOCIAL_NETWORKS)
  private socialNetworksAPI: SocialNetworksAPI;

  @OnMount(Aliases.LOGIN_COMPONENT)
  onLoginMount(): void {
    ThemeService.setContentTonalRange(ProviderAccreditationConfigurationService.getTonalidadeLogin());

    void ProviderAccreditationConfigurationService.getConfigurationData().then(data => {
      this.loginAPI.setLogo(data.dsUrlLogoHeaderLogin as string);
      const info = `${data.dsEndereco || ''}
      ${data.nrFoneContato ? MessageService.msg('pa.telephone') + ': ' : ''}  ${data.nrFoneContato || ''}
      ${data.dsEmailRodape ? MessageService.msg('pa.email') + ': ' : ''} ${data.dsEmailRodape || ''}`;
      this.textInfoAPI.setValue(info);

      data.dsLinkFacebook && this.socialNetworksAPI.setFacebook(data.dsLinkFacebook as string);
      data.dsLinkLinkedin && this.socialNetworksAPI.setLinkedin(data.dsLinkLinkedin as string);
      data.dsLinkYoutube && this.socialNetworksAPI.setYoutube(data.dsLinkYoutube as string);
      data.dsLinkInstagram && this.socialNetworksAPI.setInstagram(data.dsLinkInstagram as string);
      data.dsLinkTwitter && this.socialNetworksAPI.setTwitter(data.dsLinkTwitter as string);
    });

    setTimeout(() => this.cookiesToast(), 1000);
  }

  private cookiesToast(): void {

    ToastService.open({
      id: 'c-toast-notification__message--cookies',
      title: msg('pa.cookies-policy'),
      message: msg('pa.message-cookie-policies'),
      dismissible: true,
      dismissStyle: DismissStyle.BUTTON,
      showIcon: true,
      dismissExpression: msg('pa.confirm'),
      action: this.getMessageCookiePolicies(),
      level: ToastLevel.WARNING,
      highlight: true,
    });

  }

  private getMessageCookiePolicies(): ReactElement {
    return (
      <Link onClick={() => this.showCookiesModal()} value={msg('pa.cookies-policy')} />
    );
  }

  showCookiesModal(): void {
    const modalWithFragment: ModalProps = {
      closeable: true,
      fragment: 'cookie-policies',
      title: 'pa.cookies-policy',
      theme: ThemeTonalRange.ULTRA_LIGHT,
    };
    const breakpoints = getBreakpoint() as string;
    if (breakpoints == 'sm' || breakpoints == 'xsm') {
      ModalService.openFullscreen(modalWithFragment);
    } else {
      ModalService.openDialog(modalWithFragment);
    }
  }

  @OnClick(Aliases.BTN_REGISTER_NEW_USER)
  onCLickNewUser(): void {
    const modalConfiguration: ModalProps =  {
      id:'screen-new-user',
      closeable: true,
      fragment: 'new-user',
      title: 'pa.registration-of-user',
      className: 'modal-new-user',
      theme: ThemeTonalRange.ULTRA_LIGHT,
    };
    if (shouldRenderMobile()) {
      ModalService.openFullscreen(modalConfiguration);
    } else {
      ModalService.openDialog(modalConfiguration);
    }
  }

  @OnCatch(Aliases.LOGIN_COMPONENT)
  onCatchLogin(event: PromiseEvent<Record<string, unknown>>): void {
    const response = event.response.response as Response;
    void response.json().then((content) => {
      this.showNotification(content);
      this.passwordExpired(content);
    });
  }

  showNotification(content: any): void {
    this.loginAPI.setNotification({
      message: msg(content.message),
      level: ToastLevel.WARNING,
      highlight: true,
    });
  }

  passwordExpired(error: any): void {
    if (error.code) {
      const codigoErro = error.code[Object.keys(error.code)[0]];
      if (codigoErro == '870103') {
        const { username } = this.loginAPI.state;
        void HttpService.get(Endpoint.GET.LOGIN_USER, { username })
          .then((response) => {
            const { nrSequence } = response.body as User;
            this.historyService.push(FOUNDATION_ROUTES.RENEW_PW_BY_USERNAME, { nrSequence });
          });
      }
    }
  }
}
